/**
 * Forgot password page component
 * Allows users to reset their password via email verification
 */

import { ArrowLeft, Check, X } from 'lucide-react';
import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { forgotPassword, confirmForgotPassword } from '@/services/auth';

type Step = 'email' | 'reset';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: 'At least 8 characters',
    test: (pwd) => pwd.length >= 8,
  },
  {
    label: 'Contains uppercase letter',
    test: (pwd) => /[A-Z]/.test(pwd),
  },
  {
    label: 'Contains lowercase letter',
    test: (pwd) => /[a-z]/.test(pwd),
  },
  {
    label: 'Contains number',
    test: (pwd) => /\d/.test(pwd),
  },
  {
    label: 'Contains special character',
    test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  },
];

export function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = React.useState<Step>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showRequirements, setShowRequirements] = React.useState(false);

  // Form validation errors
  const [emailError, setEmailError] = React.useState('');
  const [codeError, setCodeError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState('');

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateCode = (value: string): boolean => {
    if (!value) {
      setCodeError('Verification code is required');
      return false;
    }
    if (value.length !== 6) {
      setCodeError('Verification code must be 6 digits');
      return false;
    }
    setCodeError('');
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }

    const allRequirementsMet = passwordRequirements.every((req) =>
      req.test(value)
    );

    if (!allRequirementsMet) {
      setPasswordError('Password does not meet all requirements');
      return false;
    }

    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      await forgotPassword({ email });
      setStep('reset');
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err instanceof Error) {
        if (err.message.includes('UserNotFoundException')) {
          setError('No account found with this email address.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate all fields
    const isCodeValid = validateCode(code);
    const isPasswordValid = validatePassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (!isCodeValid || !isPasswordValid || !isConfirmPasswordValid) {
      return;
    }

    setIsLoading(true);

    try {
      await confirmForgotPassword({
        email,
        confirmationCode: code,
        newPassword,
      });
      // Navigate to login with success message
      navigate('/login', {
        state: { message: 'Password reset successfully! Please sign in.' },
      });
    } catch (err) {
      console.error('Reset password error:', err);
      if (err instanceof Error) {
        if (err.message.includes('CodeMismatchException')) {
          setError('Invalid verification code. Please try again.');
        } else if (err.message.includes('ExpiredCodeException')) {
          setError('Verification code has expired. Please request a new one.');
          setStep('email');
        } else if (err.message.includes('InvalidPasswordException')) {
          setError('Password does not meet security requirements.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 'email' ? 'Reset password' : 'Create new password'}
      subtitle={
        step === 'email'
          ? 'Enter your email to receive a verification code'
          : `Enter the code sent to ${email}`
      }
    >
      <div className="rounded-lg border border-border-subtle bg-surface p-8 shadow-md">
          {/* Error Alert */}
          {error && (
            <div
              className="mb-6 rounded-md border border-danger/20 bg-danger-soft px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Step 1: Email Input */}
          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <Input
                id="email"
                type="email"
                label="Email address"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) validateEmail(e.target.value);
                }}
                onBlur={() => validateEmail(email)}
                error={!!emailError}
                errorMessage={emailError}
                disabled={isLoading}
                autoComplete="email"
                required
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Sending code...' : 'Send verification code'}
              </Button>
            </form>
          )}

          {/* Step 2: Reset Password Form */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              {/* Verification Code Input */}
              <Input
                id="code"
                type="text"
                label="Verification code"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                  if (codeError) validateCode(value);
                }}
                onBlur={() => validateCode(code)}
                error={!!codeError}
                errorMessage={codeError}
                disabled={isLoading}
                autoComplete="off"
                maxLength={6}
                required
              />

              {/* New Password Input */}
              <div>
                <Input
                  id="newPassword"
                  type="password"
                  label="New password"
                  placeholder="Create a strong password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordError) validatePassword(e.target.value);
                  }}
                  onFocus={() => setShowRequirements(true)}
                  onBlur={() => {
                    validatePassword(newPassword);
                    setShowRequirements(false);
                  }}
                  error={!!passwordError}
                  errorMessage={passwordError}
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                />

                {/* Password Requirements */}
                {(showRequirements || passwordError) && newPassword && (
                  <div className="mt-3 rounded-md border border-border-subtle bg-surface-subtle p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
                      Password Requirements
                    </p>
                    <ul className="space-y-2" role="list">
                      {passwordRequirements.map((req, index) => {
                        const isMet = req.test(newPassword);
                        return (
                          <li
                            key={index}
                            className="flex items-center gap-2 text-xs"
                          >
                            {isMet ? (
                              <Check className="h-3 w-3 text-success" />
                            ) : (
                              <X className="h-3 w-3 text-muted" />
                            )}
                            <span
                              className={cn(
                                isMet ? 'text-success' : 'text-secondary'
                              )}
                            >
                              {req.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Confirm Password Input */}
              <Input
                id="confirmPassword"
                type="password"
                label="Confirm new password"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmPasswordError)
                    validateConfirmPassword(e.target.value);
                }}
                onBlur={() => validateConfirmPassword(confirmPassword)}
                error={!!confirmPasswordError}
                errorMessage={confirmPasswordError}
                disabled={isLoading}
                autoComplete="new-password"
                required
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Resetting password...' : 'Reset password'}
              </Button>

              {/* Back to Email Step */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError(null);
                  }}
                  disabled={isLoading}
                  className="text-sm font-medium text-secondary hover:text-primary transition-colors"
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>

      {/* Back to Login Link */}
      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>
      </div>
    </AuthLayout>
  );
}
